import { useMemo, useCallback } from "react";
import { AppConstants } from "../../../constants/constant";
import {
  FormValues,
  Step,
  Element,
  Field,
} from "../../../Interface/scheduler/form";
import { useFormContext } from "../../../common/contexts/FormContext";

interface ShelfStorageConfigResult {
  updatedStepConfig: Step;
  hasShelfStorageField: boolean;
  configureShelfStorage: () => void;
}

interface UseShelfStorageConfigProps {
  currentStepConfig: Step | undefined;
}

export const useShelfStorageConfig = ({
  currentStepConfig,
}: UseShelfStorageConfigProps): ShelfStorageConfigResult => {
  const { formValues, setFormValues } = useFormContext();

  const { updatedStepConfig, hasShelfStorageField } = useMemo(() => {
    if (!currentStepConfig?.elements?.length) {
      return {
        updatedStepConfig: currentStepConfig || ({} as Step),
        hasShelfStorageField: false,
      };
    }

    const updatedStepConfig: Step = createDeepCopy(currentStepConfig);
    let hasShelfStorageField = false;

    updatedStepConfig.elements.forEach((element) => {
      if (!isValidFormElement(element)) return;

      const shelfStorageFieldIndex = findShelfStorageFieldIndex(element.fields);
      if (shelfStorageFieldIndex === -1) return;

      hasShelfStorageField = true;

      if (shouldRemoveShelfStorageField(formValues)) {
        removeShelfStorageField(element);
      }
    });
    return { updatedStepConfig, hasShelfStorageField };
  }, [
    currentStepConfig,
    formValues.onlineScheduling,
    formValues.offersClosetAppointments,
  ]);

  // Callback for configuring shelf storage
  const configureShelfStorage = useCallback(() => {
    if (hasShelfStorageField && !formValues.shelfStorageConfigure) {
      setFormValues((prevValues) => ({
        ...prevValues,
        shelfStorageConfigure: true,
      }));
    }
  }, [hasShelfStorageField, formValues.shelfStorageConfigure, setFormValues]);

  return {
    updatedStepConfig,
    hasShelfStorageField,
    configureShelfStorage,
  };
};

// Helper functions for better readability and testing
const createDeepCopy = (stepConfig: Step): Step => ({
  ...stepConfig,
  elements:
    stepConfig.elements?.map((element) => ({
      ...element,
      fields: element.fields ? [...element.fields] : undefined,
    })) || [],
});

const isValidFormElement = (element: Element): boolean =>
  element.type === AppConstants.AGNOSTIC_FORM && element.fields?.length > 0;

const findShelfStorageFieldIndex = (fields: Field[]): number =>
  fields.findIndex(
    (field: Field) => field.id === AppConstants.SHELF_STORAGE_ID,
  );

const shouldRemoveShelfStorageField = (formValues: FormValues): boolean =>
  !formValues.onlineScheduling || !formValues.offersClosetAppointments;

const removeShelfStorageField = (element: Element): void => {
  element.fields = element.fields.filter(
    (field: Field) => field.id !== AppConstants.SHELF_STORAGE_ID,
  );
};
